/**
 * Global Thumbnail Failure Tracker
 * 
 * Tracks failed thumbnail fetches across the entire application to prevent
 * repeated requests for thumbnails that have already failed.
 * 
 * This is a singleton that maintains a Set of failed thumbnail keys.
 */

class ThumbnailFailureTracker {
    constructor() {
        // Set to track failed thumbnail keys
        this.failedThumbnails = new Set();
    }

    /**
     * Generate a unique key for a thumbnail
     * @param {string} postId - Post ID
     * @param {string} tnPath - Thumbnail path
     * @returns {string} Unique key
     */
    generateKey(postId, tnPath) {
        return `${postId}:${tnPath}`;
    }

    /**
     * Mark a thumbnail as failed
     * @param {string} postId - Post ID
     * @param {string} tnPath - Thumbnail path
     */
    markAsFailed(postId, tnPath) {
        const key = this.generateKey(postId, tnPath);
        this.failedThumbnails.add(key);
        console.log(`[ThumbnailFailureTracker] Marked as failed: ${key}`);
    }

    /**
     * Check if a thumbnail has previously failed
     * @param {string} postId - Post ID
     * @param {string} tnPath - Thumbnail path
     * @returns {boolean} True if thumbnail has failed before
     */
    hasFailed(postId, tnPath) {
        const key = this.generateKey(postId, tnPath);
        return this.failedThumbnails.has(key);
    }

    /**
     * Clear a specific failed thumbnail (for retry)
     * @param {string} postId - Post ID
     * @param {string} tnPath - Thumbnail path
     */
    clearFailed(postId, tnPath) {
        const key = this.generateKey(postId, tnPath);
        this.failedThumbnails.delete(key);
        console.log(`[ThumbnailFailureTracker] Cleared failed status: ${key}`);
    }

    /**
     * Clear all failed thumbnails
     */
    clearAll() {
        this.failedThumbnails.clear();
        console.log('[ThumbnailFailureTracker] Cleared all failed thumbnails');
    }

    /**
     * Get statistics
     * @returns {Object} Stats
     */
    getStats() {
        return {
            failedCount: this.failedThumbnails.size,
            failedKeys: Array.from(this.failedThumbnails)
        };
    }
}

// Export singleton instance
export const thumbnailFailureTracker = new ThumbnailFailureTracker();
export default thumbnailFailureTracker;
